import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { X, Plus, Trash2, Gift, Search, ImageOff } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const API = "/api";
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `/api${url.startsWith("/") ? url : "/" + url}`;
};

const fmtCurrency = (v) =>
  `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CHANNELS = [
  { key: "Cardapio", label: "Cardápio (balcão/KDS)" },
  { key: "CardapioPublico", label: "Cardápio Público (web)" },
  { key: "Delivery", label: "Delivery (novo pedido manual)" },
  { key: "Balcao", label: "Balcão / Totem" },
  { key: "Mesas", label: "Mesas" },
  { key: "WhatsApp", label: "WhatsApp (bot)" },
];

const emptyItem = () => ({
  product_id: null,
  applies_to: "all",
  discount_type: "percentage",
  discount_value: 10,
  min_order_value: 0,
  weight: 5,
  description: "",
});

export default function CouponRouletteConfigModal({ campaign, onClose, onSave }) {
  const cfg = campaign?.config || {};
  const [numCoupons, setNumCoupons] = useState(cfg.num_coupons_per_order || 3);
  const [validityDays, setValidityDays] = useState(cfg.validity_days || 30);
  const [channels, setChannels] = useState(() => ({
    Cardapio: true, CardapioPublico: true, Delivery: true,
    Balcao: false, Mesas: false, WhatsApp: false,
    ...(cfg.channels || {}),
  }));
  const [sendViaWhatsapp, setSendViaWhatsapp] = useState(
    cfg.send_via_whatsapp !== false
  );
  const [pool, setPool] = useState(() => {
    const arr = Array.isArray(cfg.pool) ? cfg.pool : [];
    return arr.length > 0 ? arr.map(p => ({ ...emptyItem(), ...p })) : [emptyItem()];
  });
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/products`, getAuthHeader())
      .then((r) => setProducts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProducts([]));
  }, []);

  const setItemField = (idx, field, value) => {
    setPool((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  const addItem = () => setPool((p) => [...p, emptyItem()]);
  const removeItem = (idx) => setPool((p) => p.filter((_, i) => i !== idx));

  const handleSave = async () => {
    // Validação básica
    const cleaned = pool
      .filter((it) => Number(it.weight) > 0 && Number(it.discount_value) > 0)
      .map((it) => ({
        product_id: it.applies_to === "products" ? it.product_id : null,
        applies_to: it.applies_to === "products" && it.product_id ? "products" : "all",
        discount_type: it.discount_type,
        discount_value: Number(it.discount_value) || 0,
        min_order_value: Number(it.min_order_value) || 0,
        weight: Math.max(1, Math.min(10, Number(it.weight) || 1)),
        description: (it.description || "").slice(0, 120),
      }));

    if (cleaned.length === 0) {
      alert("Adicione pelo menos 1 prêmio com peso e valor maiores que zero.");
      return;
    }

    setSaving(true);
    try {
      const newConfig = {
        ...cfg,
        num_coupons_per_order: Math.max(1, Math.min(5, Number(numCoupons) || 3)),
        validity_days: Math.max(1, Math.min(90, Number(validityDays) || 30)),
        channels,
        send_via_whatsapp: !!sendViaWhatsapp,
        pool: cleaned,
      };
      await onSave(newConfig);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-2xl border shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-card z-10 rounded-t-2xl">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Gift className="w-5 h-5 text-purple-500" />
            Configurar Cupons Cortesia
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Regras gerais */}
          <section>
            <h3 className="text-sm font-semibold mb-2">Regras gerais</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Cupons por pedido</label>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  value={numCoupons}
                  onChange={(e) => setNumCoupons(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Validade (dias)</label>
                <Input
                  type="number"
                  min={1}
                  max={90}
                  value={validityDays}
                  onChange={(e) => setValidityDays(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </section>

          {/* Canais */}
          <section>
            <h3 className="text-sm font-semibold mb-2">Canais onde gerar</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Apenas pedidos criados nesses módulos disparam o sorteio quando forem concluídos.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CHANNELS.map((ch) => (
                <label
                  key={ch.key}
                  className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!channels[ch.key]}
                    onChange={(e) =>
                      setChannels((prev) => ({ ...prev, [ch.key]: e.target.checked }))
                    }
                    className="w-4 h-4 accent-purple-500"
                  />
                  <span className="text-sm">{ch.label}</span>
                </label>
              ))}
            </div>
          </section>

          {/* Pool de prêmios */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">Pool de prêmios</h3>
              <Button variant="outline" size="sm" onClick={addItem} className="gap-1.5">
                <Plus className="w-3.5 h-3.5" /> Adicionar
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              O sistema sorteia <strong>{numCoupons}</strong> cupons distintos por pedido,
              respeitando o peso (maior peso = maior chance).
            </p>
            <div className="space-y-3">
              {pool.map((item, idx) => (
                <div key={idx} className="rounded-xl border p-3 space-y-3 bg-muted/10">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">
                      Prêmio #{idx + 1}
                    </span>
                    <button
                      onClick={() => removeItem(idx)}
                      disabled={pool.length === 1}
                      className="p-1 text-red-500 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-40"
                      title="Remover"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Aplica em: geral ou produto específico (com busca) */}
                  <div>
                    <label className="text-xs text-muted-foreground">Aplica em</label>
                    <div className="mt-1 flex gap-2">
                      <select
                        value={item.applies_to}
                        onChange={(e) => {
                          const v = e.target.value;
                          setItemField(idx, "applies_to", v);
                          if (v === "all") setItemField(idx, "product_id", null);
                        }}
                        className="h-9 px-2 rounded-md border bg-card text-sm w-44"
                      >
                        <option value="all">Qualquer pedido</option>
                        <option value="products">Produto específico</option>
                      </select>
                      {item.applies_to === "products" && (
                        <div className="flex-1 min-w-0">
                          <ProductPicker
                            products={products}
                            selectedId={item.product_id}
                            onSelect={(p) => setItemField(idx, "product_id", p ? p.id : null)}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Desconto */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground">Tipo</label>
                      <select
                        value={item.discount_type}
                        onChange={(e) => setItemField(idx, "discount_type", e.target.value)}
                        className="mt-1 w-full h-9 px-2 rounded-md border bg-card text-sm"
                      >
                        <option value="percentage">% OFF</option>
                        <option value="fixed">R$ OFF</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">
                        {item.discount_type === "percentage" ? "Valor (%)" : "Valor (R$)"}
                      </label>
                      <Input
                        type="number"
                        min={0}
                        step={item.discount_type === "percentage" ? 1 : 0.5}
                        value={item.discount_value}
                        onChange={(e) => setItemField(idx, "discount_value", e.target.value)}
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Mín. pedido (R$)</label>
                      <Input
                        type="number"
                        min={0}
                        step={0.5}
                        value={item.min_order_value}
                        onChange={(e) => setItemField(idx, "min_order_value", e.target.value)}
                        className="mt-1 h-9"
                      />
                    </div>
                  </div>

                  {/* Peso + descrição */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground">Peso (chance)</label>
                      <Input
                        type="number"
                        min={1}
                        max={10}
                        value={item.weight}
                        onChange={(e) => setItemField(idx, "weight", e.target.value)}
                        className="mt-1 h-9"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-muted-foreground">Descrição (aparece no recibo/WhatsApp)</label>
                      <Input
                        type="text"
                        maxLength={120}
                        placeholder="Ex.: 10% OFF em Coca-Cola 2L"
                        value={item.description}
                        onChange={(e) => setItemField(idx, "description", e.target.value)}
                        className="mt-1 h-9"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* WhatsApp */}
          <section>
            <label className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={sendViaWhatsapp}
                onChange={(e) => setSendViaWhatsapp(e.target.checked)}
                className="w-4 h-4 accent-purple-500"
              />
              <span className="text-sm">Enviar cupons também pelo WhatsApp do cliente</span>
            </label>
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t bg-muted/20 sticky bottom-0 rounded-b-2xl">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="gap-1.5 bg-purple-600 hover:bg-purple-700"
          >
            {saving ? "Salvando..." : "Salvar configuração"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Picker de produto com busca por digitação.
 * Mostra preview com foto + nome + valor. Ao selecionar, exibe como chip.
 * Clicar no chip abre de novo para trocar. Lista limitada a 8 resultados por performance.
 */
function ProductPicker({ products, selectedId, onSelect }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selected = useMemo(
    () => (selectedId ? products.find((p) => p.id === selectedId) : null),
    [products, selectedId]
  );

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = products.filter((p) => p.is_available !== false && p.is_ingrediente !== 1 && p.product_type !== "receita");
    if (!q) return list.slice(0, 8);
    return list
      .filter((p) => (p.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [products, query]);

  if (selected && !open) {
    const photo = getImageUrl(selected.photo_url);
    const price = selected.sale_price ?? selected.simple_price ?? 0;
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); setQuery(""); }}
        className="w-full h-9 flex items-center gap-2 px-2 rounded-md border bg-card text-sm hover:bg-muted/40 transition-colors text-left"
      >
        {photo ? (
          <img src={photo} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div className="w-7 h-7 rounded bg-muted flex items-center justify-center flex-shrink-0">
            <ImageOff className="w-3.5 h-3.5 text-muted-foreground/40" />
          </div>
        )}
        <span className="flex-1 truncate font-medium">{selected.name}</span>
        <span className="text-xs text-muted-foreground flex-shrink-0">{fmtCurrency(price)}</span>
        <X className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" onClick={(e) => { e.stopPropagation(); onSelect(null); }} />
      </button>
    );
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar produto..."
          className="w-full h-9 pl-7 pr-2 rounded-md border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/40"
          autoFocus={!!selected}
        />
      </div>

      {open && filtered.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-card border rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
          {filtered.map((p) => {
            const photo = getImageUrl(p.photo_url);
            const price = p.sale_price ?? p.simple_price ?? 0;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => { onSelect(p); setOpen(false); setQuery(""); }}
                className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-muted/60 text-left border-b last:border-b-0"
              >
                {photo ? (
                  <img src={photo} alt="" className="w-9 h-9 rounded object-cover flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <div className="w-9 h-9 rounded bg-muted flex items-center justify-center flex-shrink-0">
                    <ImageOff className="w-4 h-4 text-muted-foreground/40" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p.name}</p>
                  {p.description && (
                    <p className="text-[11px] text-muted-foreground truncate">{p.description}</p>
                  )}
                </div>
                <span className="text-xs font-semibold text-orange-500 flex-shrink-0">
                  {fmtCurrency(price)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {open && filtered.length === 0 && query && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-card border rounded-lg shadow-lg z-50 p-3 text-center text-xs text-muted-foreground">
          Nenhum produto encontrado para "{query}"
        </div>
      )}
    </div>
  );
}
